// JS
import React from 'react';
import {
  randomId,
  getPreviousBreakpoint,
  useTailwindMediaQuery
} from '../shared/utils';

import { Swiper, SwiperSlide } from 'swiper/react';

import {
  Navigation,
  Pagination,
  Scrollbar,
  Grid,
  EffectFade,
  Autoplay
} from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/grid';
import 'swiper/css/effect-fade';

import { chevronLeft, chevronRight } from '../shared/svgData';

const Slider = ({
  name = null,
  responsive = null,
  infinite = false,
  matchHeight = true, // True by default, we want all slides to be the same height
  useNavigation = false,
  usePagination = false,
  useScrollbar = false,
  useGrid = false,
  useFade = false,
  useAutoplay = false,
  paginationOptions = {},
  children
}) => {
  const uniqueId = randomId();
  const isMobile = useTailwindMediaQuery('lg');

  const previousArrow = (
    <a
      data-previous-arrow={uniqueId}
      className="absolute top-0 left-0 h-full z-10 text-heroarrows flex flex-col items-center justify-center pl-8 lg:pl-14 pr-14 lg:pr-20 text-arrow cursor-pointer opacity-30 hover:opacity-100 transition-opacity ease-in duration-250"
    >
      <span className="w-14 h-14">{chevronLeft}</span>
    </a>
  );

  const nextArrow = (
    <a
      data-next-arrow={uniqueId}
      className="absolute top-0 right-0 h-full z-10 text-heroarrows flex flex-col items-center justify-center pl-14 lg:pl-20 pr-8 lg:pr-14 text-arrow cursor-pointer opacity-30 hover:opacity-100 transition-opacity ease-in duration-250"
    >
      <span className="w-14 h-14">{chevronRight}</span>
    </a>
  );

  const navigation = {
    prevEl: `[data-previous-arrow="${uniqueId}"]`,
    nextEl: `[data-next-arrow="${uniqueId}"]`
  };

  const pagination = {
    el: `[data-pagination="${uniqueId}"]`,
    clickable: true,
    renderBullet: function (index, className) {
      if (paginationOptions.rounded) {
        return `<span class="block only:hidden relative my-3 mr-1.5 w-4 h-4 max-w-[6rem] bg-transparent cursor-pointer after:absolute after:max-w-[6rem] after:w-4 after:h-4 after:top-50 after:rounded-full ${className}"></span>`;
      } else {
        return `<span class="block only:hidden relative py-6 mx-3 first:ml-0 last:mr-0 w-full h-1 max-w-[6rem] bg-transparent cursor-pointer after:absolute after:max-w-[6rem] after:w-full after:h-1 after:top-50 ${className}"></span>`;
      }
    },
    bulletClass: 'after:bg-gray-200',
    bulletActiveClass: 'after:bg-black'
  };

  const modules = [];
  if (useNavigation) modules.push(Navigation);
  if (usePagination) modules.push(Pagination);
  if (useScrollbar) modules.push(Scrollbar);
  if (useGrid) modules.push(Grid);
  if (useFade) modules.push(EffectFade);
  if (useAutoplay) modules.push(Autoplay);

  let breakpoints = {};
  if (responsive != null) {
    var lastObj = {};
    for (var key in responsive) {
      var twBreakpoint = getPreviousBreakpoint(key);
      var item = responsive[key];

      // Inherit all the previous attributes from the last breakpoint created
      var obj = JSON.parse(JSON.stringify(lastObj));
      for (var itemKey in item) {
        if (itemKey === 'show') obj.slidesPerView = item[itemKey];
        else obj[itemKey] = item[itemKey];
      }
      Object.assign(breakpoints, { [twBreakpoint]: obj });
      lastObj = obj;
    }
  }

  return (
    <div
      id={(name != null ? name : 'swiper') + '-' + uniqueId}
      className="clearfix"
    >
      <Swiper
        modules={modules}
        breakpoints={breakpoints}
        loop={infinite}
        loopedSlides={children && children.length}
        pagination={usePagination && pagination}
        navigation={useNavigation && navigation}
        grid={
          useGrid && {
            fill: 'row',
            rows: 3
          }
        }
        autoplay={
          useAutoplay && {
            delay: 6000,
            disableOnInteraction: false
          }
        }
        effect={useFade && 'fade'}
        scrollbar={{
          el: `[data-scrollbar="${uniqueId}"]`,
          draggable: true
        }}
        className="relative"
      >
        {useNavigation && !isMobile && previousArrow}

        {children != null &&
          children.map((child, index) => {
            return (
              <SwiperSlide
                key={uniqueId + index}
                className={'p-0 cursor-auto' + (matchHeight ? ' h-auto' : '')}
              >
                {child}
              </SwiperSlide>
            );
          })}

        {useNavigation && !isMobile && nextArrow}
      </Swiper>

      {useScrollbar && (
        <div
          data-scrollbar={uniqueId}
          className="h-[4px] bg-gray-200 mt-2 md:mt-4 rounded-full"
        ></div>
      )}

      {usePagination && (
        <div
          data-pagination={uniqueId}
          className={
            (paginationOptions.noContain ? '' : 'container ') +
            'flex justify-center items-center mt-8 md:mt-12 ' +
            (paginationOptions?.classes?.wrapper
              ? ' ' + paginationOptions.classes.wrapper
              : '')
          }
        ></div>
      )}
    </div>
  );
};

export default Slider;
